export type HintChip = "A" | "B" | "X" | "Y" | "LB" | "RB" | "RT" | "LT";

export type KeyType =
  | "char"
  | "backspace"
  | "enter"
  | "space"
  | "shift"
  | "symbols"
  | "hide"
  | "cursorLeft"
  | "cursorRight";

export type KeyDef = {
  id: string;
  type: KeyType;
  label: string;
  value?: string;
  shiftLabel?: string;
  shiftValue?: string;
  hint?: HintChip;
  col: number;
  row: number;
  colSpan?: number;
  rowSpan?: number;
};

// 26-col grid: 1 "unit" = 2 cols. Lets cursor arrows each span 3 cols
// (1.5 units) so left/right are equal under a 3-unit-wide Enter / Backspace.
export const COLS = 26;
export const ROWS = 5;

const UNIT = 2;
const unitCol = (i: number) => i * UNIT + 1; // i is 0-based unit index

const numberRow: KeyDef[] = "1234567890".split("").map((c, i) => ({
  id: `num-${c}`,
  type: "char",
  label: c,
  col: unitCol(i),
  row: 1,
  colSpan: UNIT,
}));

const topLetters: KeyDef[] = "qwertyuiop".split("").map((c, i) => ({
  id: `k-${c}`,
  type: "char",
  label: c,
  shiftLabel: c.toUpperCase(),
  col: unitCol(i),
  row: 2,
  colSpan: UNIT,
}));

const midLetters: KeyDef[] = [..."asdfghjkl", "@"].map((c, i) => ({
  id: `k-${c}`,
  type: "char",
  label: c,
  shiftLabel: c === "@" ? "@" : c.toUpperCase(),
  col: unitCol(i),
  row: 3,
  colSpan: UNIT,
}));

const bottomLetters: KeyDef[] = [..."zxcvbnm", ",", ".", "?"].map((c, i) => ({
  id: `k-${c === "," ? "comma" : c === "." ? "dot" : c === "?" ? "qmark" : c}`,
  type: "char",
  label: c,
  shiftLabel: c === "," || c === "." || c === "?" ? c : c.toUpperCase(),
  col: unitCol(i),
  row: 4,
  colSpan: UNIT,
}));

export const lettersLayout: KeyDef[] = [
  ...numberRow,
  {
    id: "backspace",
    type: "backspace",
    label: "⌫",
    hint: "X",
    col: 21,
    row: 1,
    colSpan: 6,
  },
  ...topLetters,
  {
    id: "cursorLeft",
    type: "cursorLeft",
    label: "‹",
    col: 21,
    row: 2,
    colSpan: 3,
  },
  {
    id: "cursorRight",
    type: "cursorRight",
    label: "›",
    col: 24,
    row: 2,
    colSpan: 3,
  },
  ...midLetters,
  {
    id: "enter",
    type: "enter",
    label: "Enter",
    hint: "RT",
    col: 21,
    row: 3,
    colSpan: 6,
    rowSpan: 2,
  },
  ...bottomLetters,
  {
    id: "shift",
    type: "shift",
    label: "↑",
    hint: "LB",
    col: 1,
    row: 5,
    colSpan: 2,
  },
  {
    id: "space",
    type: "space",
    label: "Space",
    hint: "Y",
    col: 3,
    row: 5,
    colSpan: 14,
  },
  {
    id: "symbols",
    type: "symbols",
    label: "*&$",
    hint: "RB",
    col: 17,
    row: 5,
    colSpan: 4,
  },
  {
    id: "hide",
    type: "hide",
    label: "Hide",
    hint: "B",
    col: 21,
    row: 5,
    colSpan: 6,
  },
];

const symbolsRow1 = "!@#$%^&*()".split("").map<KeyDef>((c, i) => ({
  id: `sym-r1-${i}`,
  type: "char",
  label: c,
  col: unitCol(i),
  row: 1,
  colSpan: UNIT,
}));

const symbolsRow2 = "-_=+[]{}\\|".split("").map<KeyDef>((c, i) => ({
  id: `sym-r2-${i}`,
  type: "char",
  label: c,
  col: unitCol(i),
  row: 2,
  colSpan: UNIT,
}));

const symbolsRow3 = ";:'\"<>,./?".split("").map<KeyDef>((c, i) => ({
  id: `sym-r3-${i}`,
  type: "char",
  label: c,
  col: unitCol(i),
  row: 3,
  colSpan: UNIT,
}));

const symbolsRow4 = "~`£€¥¢©®°±".split("").map<KeyDef>((c, i) => ({
  id: `sym-r4-${i}`,
  type: "char",
  label: c,
  col: unitCol(i),
  row: 4,
  colSpan: UNIT,
}));

export const symbolsLayout: KeyDef[] = [
  ...symbolsRow1,
  {
    id: "backspace",
    type: "backspace",
    label: "⌫",
    hint: "X",
    col: 21,
    row: 1,
    colSpan: 6,
  },
  ...symbolsRow2,
  {
    id: "cursorLeft",
    type: "cursorLeft",
    label: "‹",
    col: 21,
    row: 2,
    colSpan: 3,
  },
  {
    id: "cursorRight",
    type: "cursorRight",
    label: "›",
    col: 24,
    row: 2,
    colSpan: 3,
  },
  ...symbolsRow3,
  {
    id: "enter",
    type: "enter",
    label: "Enter",
    hint: "RT",
    col: 21,
    row: 3,
    colSpan: 6,
    rowSpan: 2,
  },
  ...symbolsRow4,
  {
    id: "shift",
    type: "shift",
    label: "↑",
    hint: "LB",
    col: 1,
    row: 5,
    colSpan: 2,
  },
  {
    id: "space",
    type: "space",
    label: "Space",
    hint: "Y",
    col: 3,
    row: 5,
    colSpan: 14,
  },
  {
    id: "symbols",
    type: "symbols",
    label: "abc",
    hint: "RB",
    col: 17,
    row: 5,
    colSpan: 4,
  },
  {
    id: "hide",
    type: "hide",
    label: "Hide",
    hint: "B",
    col: 21,
    row: 5,
    colSpan: 6,
  },
];
